/**
 * Performance Monitoring Utility
 * 
 * Provides utilities for tracking and analyzing application performance metrics
 * including first screen load, Toy load times, and other key performance indicators.
 * 
 * Compatible with Chrome DevTools Performance and Lighthouse testing.
 */

// Performance metrics storage
const metrics = {
  marks: new Map(),
  measures: new Map(),
  customMetrics: new Map()
}

/**
 * Check if Performance API is available
 */
function isPerformanceSupported() {
  return typeof window !== 'undefined' && 
         'performance' in window && 
         'mark' in window.performance
}

/**
 * Create a performance mark
 * @param {string} name - Mark name
 */
export function mark(name) {
  if (!isPerformanceSupported()) return
  
  try {
    performance.mark(name)
    metrics.marks.set(name, performance.now())
  } catch (error) {
    console.warn(`Failed to create performance mark: ${name}`, error)
  }
}

/**
 * Measure time between two marks
 * @param {string} name - Measure name
 * @param {string} startMark - Start mark name
 * @param {string} endMark - End mark name (optional, defaults to now)
 * @returns {number} Duration in milliseconds
 */
export function measure(name, startMark, endMark) {
  if (!isPerformanceSupported()) return 0
  
  try {
    if (endMark) {
      performance.measure(name, startMark, endMark)
    } else {
      performance.measure(name, startMark)
    }
    
    const entries = performance.getEntriesByName(name, 'measure')
    if (entries.length > 0) {
      const duration = entries[entries.length - 1].duration
      metrics.measures.set(name, duration)
      return duration
    }
  } catch (error) {
    console.warn(`Failed to measure performance: ${name}`, error)
  }
  
  return 0
}

/**
 * Track custom metric
 * @param {string} name - Metric name
 * @param {number} value - Metric value
 * @param {string} unit - Unit of measurement (optional)
 */
export function trackMetric(name, value, unit = 'ms') {
  metrics.customMetrics.set(name, { value, unit, timestamp: Date.now() })
  
  if (isPerformanceSupported()) {
    console.log(`📊 Performance Metric: ${name} = ${value}${unit}`)
  }
}

/**
 * Get all performance metrics
 * @returns {Object} All collected metrics
 */
export function getMetrics() {
  return {
    marks: metrics.marks,
    measures: metrics.measures,
    custom: metrics.customMetrics
  }
}

/**
 * Clear all performance metrics
 */
export function clearMetrics() {
  if (isPerformanceSupported()) {
    performance.clearMarks()
    performance.clearMeasures()
  }
  
  metrics.marks.clear()
  metrics.measures.clear()
  metrics.customMetrics.clear()
}

/**
 * Track Toy loading performance
 * @param {string} toyId - Toy identifier
 * @returns {Object} Tracking functions
 */
export function trackToyLoad(toyId) {
  const startMarkName = `toy-load-start-${toyId}`
  const endMarkName = `toy-load-end-${toyId}`
  const measureName = `toy-load-${toyId}`
  
  mark(startMarkName)
  
  return {
    end: () => {
      mark(endMarkName)
      const duration = measure(measureName, startMarkName, endMarkName)
      trackMetric(`Toy Load: ${toyId}`, duration)
      return duration
    }
  }
}

/**
 * Track component render performance
 * @param {string} componentName - Component name
 * @returns {Object} Tracking functions
 */
export function trackComponentRender(componentName) {
  const startMarkName = `component-render-start-${componentName}`
  const endMarkName = `component-render-end-${componentName}`
  const measureName = `component-render-${componentName}`
  
  mark(startMarkName)
  
  return {
    end: () => {
      mark(endMarkName)
      const duration = measure(measureName, startMarkName, endMarkName)
      trackMetric(`Component Render: ${componentName}`, duration)
      return duration
    }
  }
}

/**
 * Get Core Web Vitals metrics
 * @returns {Promise<Object>} Core Web Vitals data
 */
export async function getCoreWebVitals() {
  if (!isPerformanceSupported()) {
    return null
  }
  
  return new Promise((resolve) => {
    const vitals = {}
    
    // Get navigation timing
    const navigation = performance.getEntriesByType('navigation')[0]
    if (navigation) {
      vitals.domContentLoaded = navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart
      vitals.loadComplete = navigation.loadEventEnd - navigation.loadEventStart
      vitals.domInteractive = navigation.domInteractive - navigation.fetchStart
    }
    
    // Get paint timing
    const paintEntries = performance.getEntriesByType('paint')
    paintEntries.forEach(entry => {
      if (entry.name === 'first-paint') {
        vitals.firstPaint = entry.startTime
      } else if (entry.name === 'first-contentful-paint') {
        vitals.firstContentfulPaint = entry.startTime
      }
    })
    
    // Try to get LCP (Largest Contentful Paint)
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        const lastEntry = entries[entries.length - 1]
        vitals.largestContentfulPaint = lastEntry.renderTime || lastEntry.loadTime
        observer.disconnect()
        resolve(vitals)
      })
      
      observer.observe({ entryTypes: ['largest-contentful-paint'] })
      
      // Timeout after 5 seconds
      setTimeout(() => {
        observer.disconnect()
        resolve(vitals)
      }, 5000)
    } catch (error) {
      resolve(vitals)
    }
  })
}

/**
 * Monitor First Screen Load performance
 */
export function monitorFirstScreenLoad() {
  if (!isPerformanceSupported()) return
  
  mark('app-init-start')
  
  // Monitor when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      mark('dom-content-loaded')
      measure('time-to-dom-ready', 'app-init-start', 'dom-content-loaded')
    })
  }
  
  // Monitor when page is fully loaded
  window.addEventListener('load', () => {
    mark('page-load-complete')
    const duration = measure('time-to-page-load', 'app-init-start', 'page-load-complete')
    trackMetric('First Screen Load', duration)
    
    // Get and log Core Web Vitals
    setTimeout(async () => {
      const vitals = await getCoreWebVitals()
      if (vitals) {
        console.log('📊 Core Web Vitals:', vitals)
        Object.entries(vitals).forEach(([key, value]) => {
          trackMetric(`Core Web Vital: ${key}`, value)
        })
      }
    }, 0)
  })
}

/**
 * Create a performance report
 * @returns {Object} Performance report
 */
export function generatePerformanceReport() {
  const report = {
    timestamp: new Date().toISOString(),
    metrics: getMetrics(),
    navigation: null,
    resources: []
  }
  
  if (isPerformanceSupported()) {
    // Navigation timing
    const navigation = performance.getEntriesByType('navigation')[0]
    if (navigation) {
      report.navigation = {
        redirectTime: navigation.redirectEnd - navigation.redirectStart,
        dnsTime: navigation.domainLookupEnd - navigation.domainLookupStart,
        tcpTime: navigation.connectEnd - navigation.connectStart,
        requestTime: navigation.responseStart - navigation.requestStart,
        responseTime: navigation.responseEnd - navigation.responseStart,
        domProcessingTime: navigation.domComplete - navigation.domLoading,
        loadTime: navigation.loadEventEnd - navigation.loadEventStart
      }
    }
    
    // Resource timing (top 10 slowest resources)
    const resources = performance.getEntriesByType('resource')
    report.resources = resources
      .map(r => ({
        name: r.name.split('/').pop(),
        type: r.initiatorType,
        duration: r.duration,
        size: r.transferSize
      }))
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10)
  }
  
  return report
}

/**
 * Log performance report to console
 */
export function logPerformanceReport() {
  const report = generatePerformanceReport()
  
  console.group('📊 Performance Report')
  console.log('Timestamp:', report.timestamp)
  
  if (report.navigation) {
    console.group('⏱️ Navigation Timing')
    Object.entries(report.navigation).forEach(([key, value]) => {
      console.log(`${key}: ${value.toFixed(2)}ms`)
    })
    console.groupEnd()
  }
  
  if (report.metrics.custom.size > 0) {
    console.group('📈 Custom Metrics')
    report.metrics.custom.forEach((metric, name) => {
      console.log(`${name}: ${metric.value.toFixed(2)}${metric.unit}`)
    })
    console.groupEnd()
  }
  
  if (report.resources.length > 0) {
    console.group('📦 Top 10 Slowest Resources')
    console.table(report.resources)
    console.groupEnd()
  }
  
  console.groupEnd()
  
  return report
}

/**
 * Setup performance monitoring for development
 */
export function setupPerformanceMonitoring() {
  if (import.meta.env.DEV) {
    console.log('🔍 Performance monitoring enabled')
    
    // Monitor first screen load
    monitorFirstScreenLoad()
    
    // Log report after page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        logPerformanceReport()
      }, 2000)
    })
    
    // Expose performance utilities to window for debugging
    window.__performance__ = {
      mark,
      measure,
      trackMetric,
      getMetrics,
      clearMetrics,
      generateReport: generatePerformanceReport,
      logReport: logPerformanceReport
    }
  }
}

// Export all utilities
export default {
  mark,
  measure,
  trackMetric,
  getMetrics,
  clearMetrics,
  trackToyLoad,
  trackComponentRender,
  getCoreWebVitals,
  monitorFirstScreenLoad,
  generatePerformanceReport,
  logPerformanceReport,
  setupPerformanceMonitoring
}
